import * as Dialog from '@radix-ui/react-dialog';
import { X, Play, BookOpen, RotateCcw, PackageOpen, BarChart3 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { KAPITEL } from './tour';
import { useTour } from './TourProvider';
import { faengtAussenklickAb } from '@/onboarding/tourSchutz';
import { UebungAbschnitt } from './UebungAbschnitt';

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * Der Ort, an dem die Tour jederzeit wieder zu finden ist.
 *
 * Bewusst schlicht gehalten: hier docken später weitere Hilfe-Inhalte an
 * (Kurzanleitungen, Ansprechpartner), aber ein halbfertiges Gerüst dafür
 * wäre heute nur im Weg. Was drin ist, ist fertig.
 */
export function HilfePanel({ open, onClose }: Props) {
  const tour = useTour();
  const qc = useQueryClient();
  const toast = useToast();

  // Die Begrüßung beim Anmelden wieder einschalten. Das ist zugleich der
  // einzige Weg, „nicht mehr zeigen“ rückgängig zu machen — und es macht
  // die Mechanik ohne Datenbankzugriff prüfbar (B108).
  async function begruessungZurueck(): Promise<void> {
    await apiFetch('/onboarding/status.php', {
      method: 'POST',
      body: JSON.stringify({ aktion: 'zuruecksetzen' })
    });
    await qc.invalidateQueries({ queryKey: ['auth'] });
    onClose();
    toast('success', 'Begrüßung wieder eingeschaltet.');
  }

  function starte(kapitel?: Parameters<typeof tour.starte>[0]): void {
    onClose();
    // Erst schließen lassen, dann starten: sonst legt sich der
    // Scheinwerfer über ein Panel, das gerade verschwindet.
    window.setTimeout(() => tour.starte(kapitel), 120);
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/30" />
        <Dialog.Content
          // Die Tour haengt am <body>: ohne diese Zeile gilt ein Klick in
          // die Sprechblase als Klick daneben und schliesst den Dialog.
          onPointerDownOutside={faengtAussenklickAb}
          onInteractOutside={faengtAussenklickAb}
          aria-describedby={undefined}
          data-tour="hilfe-panel"
          className="dialog-mobil-vollbild fixed left-1/2 top-1/2 z-50 w-[min(32rem,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-md border border-line bg-cream p-5 shadow-lg"
        >
          <div className="flex items-start justify-between gap-4">
            <Dialog.Title className="font-serif text-lg text-ink">Hilfe</Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Schließen"
                className="inline-flex h-11 w-11 items-center justify-center rounded text-ink-soft hover:bg-cream-dark"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          <p className="mt-2 text-sm text-ink-soft">
            Die Einführungstour zeigt dir das System an einer Übungsbestellung, die nur
            du siehst und die danach wieder verschwindet.
          </p>

          <button
            type="button"
            onClick={() => starte()}
            className="mt-4 inline-flex min-h-[44px] w-full items-center gap-2 rounded-md bg-akzent px-4 py-2.5 text-sm font-medium text-cream hover:bg-akzent-stark"
          >
            <Play className="h-4 w-4" />
            Tour von vorn starten
          </button>

          <h3 className="mt-5 text-xs uppercase tracking-wide text-ink-faint">
            Einzelne Kapitel
          </h3>
          <ul className="mt-2 space-y-1">
            {KAPITEL.map((k) => (
              <li key={k.id}>
                <button
                  type="button"
                  onClick={() => starte(k.id)}
                  className="flex w-full min-h-[44px] items-start gap-3 rounded-md px-3 py-2 text-left hover:bg-cream-dark"
                >
                  <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-ink-faint" />
                  <span>
                    <span className="block text-sm text-ink">{k.titel}</span>
                    <span className="block text-xs text-ink-faint">{k.kurz}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <p className="mt-4 text-xs text-ink-faint">
            Die Übungs-Kapitel legen dafür jeweils eine frische Übungsbestellung an.
          </p>

          {/* A53: Der Ort zum Ausprobieren steht bei der Tour, nicht
              irgendwo — beides führt über dieselbe Übungsbestellung, und
              wer etwas üben will, sucht hier. */}
          <UebungAbschnitt onClose={onClose} />

          <div className="mt-5 border-t border-line pt-4">
            <h3 className="flex items-center gap-2 text-xs uppercase tracking-wide text-ink-faint">
              <PackageOpen className="h-4 w-4" />
              Ware eintragen
            </h3>
            <p className="mt-2 text-sm text-ink-soft">
              Beim Auspacken gehst du den umgekehrten Weg: du hast das Buch und
              suchst die Bestellung. Tipp auf dem Dashboard auf „Wareneingang“ und
              scann den Strichcode vom Buchrücken. Du siehst dann, wer darauf
              wartet, seit wann die Bestellung läuft und wieviel noch fehlt — ein
              Tipp auf „Eingetroffen“ trägt ein Stück ein.
            </p>
            <p className="mt-2 text-sm text-ink-soft">
              Wurde bei mehreren Lieferanten bestellt, fragt das System, von wem es
              kam. Das entscheidet niemand außer dir: geraten wäre die Zuordnung
              wertlos. Wartet niemand auf das Buch, sagt es das ebenfalls — dann
              gehört es in den Bestand.
            </p>
            <p className="mt-2 text-sm text-ink-soft">
              Nach jedem Eintrag ist der Scanner sofort wieder bereit. Die Kamera
              bleibt dabei an, du kannst also Buch für Buch durch die Kiste gehen.
            </p>
          </div>

          {/* Nutzungsmessung (02.11.2026): Das Team soll in der App selbst
              nachlesen können, was gezählt wird — mit genau der Liste aus
              dem Konzept. */}
          <div className="mt-5 border-t border-line pt-4" data-testid="hilfe-nutzung">
            <h3 className="flex items-center gap-2 text-xs uppercase tracking-wide text-ink-faint">
              <BarChart3 className="h-4 w-4" />
              Was gemessen wird
            </h3>
            <p className="mt-2 text-sm text-ink-soft">
              Das Tracking zählt anonym, wo die Arbeit hakt, damit wir es verbessern
              können. Gespeichert wird nie, wer gearbeitet hat — nur was passiert ist,
              der Tag (ohne Uhrzeit) und ob es am PC, Tablet oder Handy war.
            </p>
            <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm text-ink-soft">
              <li>Suchen ohne Treffer, mit dem Suchbegriff</li>
              <li>wie lange es dauert, bis die richtige Bestellung offen ist</li>
              <li>Bestellungen, die sofort wieder verlassen werden</li>
              <li>abgebrochene Rückfragen und weggeklickte Warnungen</li>
              <li>Fehlermeldungen, ohne Namen und Nummern</li>
              <li>wie oft bestimmte Knöpfe benutzt werden</li>
              <li>wie lange eine neue Bestellung zum Erfassen braucht</li>
              <li>welche Seiten geöffnet werden, und wie schnell das Dashboard lädt</li>
            </ul>
            <p className="mt-2 text-sm text-ink-soft">
              Nicht gemessen werden Mausbewegungen, Scrollen, andere Eingaben oder der
              Bildschirm. Suchbegriffe werden nach 30 Tagen gelöscht, alles andere nach
              90. Die Auswertung steht in den Einstellungen unter „Nutzung“.
            </p>
          </div>

          <div className="mt-4 border-t border-line pt-3">
            <button
              type="button"
              data-tour="hilfe-begruessung"
              onClick={() => void begruessungZurueck()}
              className="flex w-full min-h-[44px] items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-ink-soft hover:bg-cream-dark hover:text-ink"
            >
              <RotateCcw className="h-4 w-4 shrink-0 text-ink-faint" />
              Begrüßung beim Anmelden wieder anzeigen
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
